import * as Api from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { IVolunteerActivity } from './models';
import { invalidateVolunteerActivities } from './useVolunteerActivities';
import { VOLUNTEER_ACTIVITY_MUTATION_SCOPE, volunteerActivityQueryKey } from './useVolunteerActivity';

export interface IUpdateVolunteerActivityCoverImageVariables {
	activity: IVolunteerActivity;
	coverImage?: Api.IFileAttachment;
	impersonationContext?: Api.IImpersonationContext;
}

export const useUpdateVolunteerActivityCoverImageMutation = ({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (data: IVolunteerActivity) => void;
} = {}) => {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		scope: VOLUNTEER_ACTIVITY_MUTATION_SCOPE,
		// The API replaces the whole document, so build the payload when the request runs (after any queued
		// activity save has landed in the cache) rather than from whatever the caller rendered.
		mutationFn: ({ activity, coverImage, impersonationContext }: IUpdateVolunteerActivityCoverImageVariables) => {
			const latest =
				queryClient.getQueryData<IVolunteerActivity>(
					volunteerActivityQueryKey(activity.id, impersonationContext?.account?.id)
				) ?? activity;
			return userSession.webServiceHelper.callAsync<IVolunteerActivity>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `volunteering/activities/${activity.id}`,
				}),
				'PUT',
				{ ...latest, coverImage }
			);
		},
		onSuccess: (data, { activity, impersonationContext }) => {
			queryClient.setQueryData(volunteerActivityQueryKey(activity.id, impersonationContext?.account?.id), data);
			invalidateVolunteerActivities();
			onSuccess?.(data);
		},
		onError,
	});
};
